import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { firefuncs } from "config/firebase";
import { httpsCallable } from "firebase/functions";
import { RootState } from "types";
import { useUserDisplayName } from "app/Hooks/useUserDisplayName";

import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";

function PrivateGameSearch() {
  const user = useSelector((state: RootState) => state.user.user);
  const { displayName } = useUserDisplayName();
  
  const [loading, setLoading] = useState(false);
  const [accessCode, setAccessCode] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error" | "">("");
  const [validationMessage, setValidationMessage] = useState("");
  const [isValidating, setIsValidating] = useState(false);

  // Debounced validation function
  const validateAccessCode = useCallback((code: string) => {
    const trimmedCode = code.trim();
    
    if (!trimmedCode) {
      setValidationMessage("");
      return;
    }

    // Check if code has the expected format (at least hostId-userCode pattern)
    const parts = trimmedCode.split('-');
    if (parts.length < 2) {
      setValidationMessage("Access code should include host prefix (e.g. abcdef-mygame)");
      return;
    }

    const [hostPrefix, ...userCodeParts] = parts;
    const userCode = userCodeParts.join('-');

    if (hostPrefix.length !== 6) {
      setValidationMessage("Invalid host prefix format");
      return;
    }

    if (userCode.length < 4) {
      setValidationMessage("User code must be at least 4 characters");
      return;
    }

    if (userCode.length > 12) {
      setValidationMessage("User code must be 12 characters or less");
      return;
    }

    setValidationMessage("");
  }, []);

  // Debounce effect
  useEffect(() => {
    setIsValidating(true);
    const timeoutId = setTimeout(() => {
      validateAccessCode(accessCode);
      setIsValidating(false);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [accessCode, validateAccessCode]);

  const handleInputChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setAccessCode(evt.target.value);
    if (message) {
      setMessage("");
      setMessageType("");
    }
  };

  const findPrivateGame = async (evt: React.FormEvent) => {
    evt.preventDefault();

    if (!accessCode.trim()) {
      setMessage("Access Code is required");
      setMessageType("error");
      return;
    }

    setLoading(true);
    setMessage("");
    setMessageType("");

    const data = {
      userId: user?.uid,
      displayName: displayName,
      accessCode: accessCode.trim(),
    };

    try {
      const addUserToPrivGame = httpsCallable(firefuncs, "addPlayerToPrivGame");
      const result = await addUserToPrivGame(data);

      setMessage(result.data?.message || "Successfully joined the game!");
      setMessageType("success");
      setAccessCode("");
    } catch (error: any) {
      console.error("Error joining private game:", error);

      // Handle Firebase Cloud Functions HttpsError codes
      if (error.code === "functions/invalid-argument") {
        setMessage(
          error.message || "Invalid access code or player information",
        );
      } else if (error.code === "functions/already-exists") {
        setMessage(error.message || "You are already in this game");
      } else if (error.code === "functions/not-found") {
        setMessage(
          error.message || "Game not found. Please check your access code.",
        );
      } else if (error.code === "functions/permission-denied") {
        setMessage("You do not have permission to join this game");
      } else if (error.code === "functions/unauthenticated") {
        setMessage("Please sign in to join a game");
      } else if (error.code === "functions/internal") {
        setMessage(error.message || "Internal server error occurred");
      } else {
        setMessage(
          error.message ||
            "Failed to join the game. Please check your access code.",
        );
      }
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 500, mx: "auto", p: 2 }}>
      <Paper
        elevation={2}
        sx={{
          p: 3,
          borderRadius: 2,
          backgroundColor: "background.paper",
        }}
      >
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            textAlign: "center",
            color: "primary.main",
            fontWeight: 500,
            mb: 3,
          }}
        >
          Enter Private Game Code
        </Typography>

        <Box component="form" onSubmit={findPrivateGame} sx={{ width: "100%" }}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              variant="outlined"
              label="Access Code"
              placeholder="Enter full access code (e.g. abcdef-mygame)"
              value={accessCode}
              onChange={handleInputChange}
              disabled={loading}
              error={!!validationMessage && !isValidating}
              helperText={
                isValidating
                  ? "Validating..."
                  : validationMessage || "Enter the complete access code provided by the host"
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={loading || !accessCode.trim() || !!validationMessage || isValidating}
              startIcon={
                loading ? <CircularProgress size={20} /> : <SearchIcon />
              }
              sx={{
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
                borderRadius: 2,
                textTransform: "none",
              }}
            >
              {loading ? "Searching..." : "Find Game"}
            </Button>

            {message && (
              <Alert
                severity={messageType === "success" ? "success" : "error"}
                sx={{
                  borderRadius: 2,
                  "& .MuiAlert-message": {
                    fontSize: "0.9rem",
                  },
                }}
              >
                {message}
              </Alert>
            )}
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}

export default PrivateGameSearch;
